import React, { useEffect } from 'react';
import { Alert, AlertDescription } from '@lb-classroom/ui';
import { CSSTransition } from 'react-transition-group';
import classnames from 'classnames';
import { X, AlertCircle, CheckCircle, AlertTriangle, Info } from 'lucide-react';

interface FlashProps {
  message?: string;
  autoDisappear?: boolean;
  clearFlashMessage: () => void;
  type?: string;
  sticky?: boolean;
}

const getAlertVariant = (type?: string) => {
  switch (type) {
    case 'error':
      return 'destructive';
    case 'success':
      return 'success';
    case 'warning':
      return 'warning';
    default:
      return 'default';
  }
};

const getAlertIcon = (type?: string) => {
  const iconClass = 'h-5 w-5';
  switch (type) {
    case 'error':
      return <AlertCircle className={iconClass} />;
    case 'success':
      return <CheckCircle className={iconClass} />;
    case 'warning':
      return <AlertTriangle className={iconClass} />;
    default:
      return <Info className={iconClass} />;
  }
};

const Flash: React.FC<FlashProps> = (props) => {
  const {
    message = '',
    autoDisappear,
    clearFlashMessage,
    type,
    sticky,
  } = props;

  useEffect(() => {
    if (autoDisappear && message.length > 0) {
      const timer = window.setTimeout(() => {
        clearFlashMessage();
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [message, autoDisappear, clearFlashMessage]);

  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    clearFlashMessage();
  };

  const wrapperClass = classnames('', { 'top-0 w-screen z-50': sticky });
  if (message.length > 0) {
    return (
      <CSSTransition appear={true} timeout={500}>
        <div className={wrapperClass} id="flash-component">
          <Alert
            variant={getAlertVariant(type)}
            className="py-3 rounded-none border-x-0 border-t-0 flex items-center gap-3 [&>svg]:static [&>svg]:translate-y-0 [&>svg~*]:pl-0"
          >
            {getAlertIcon(type)}
            <div className="flex justify-between items-center flex-1">
              <AlertDescription>{message}</AlertDescription>
              <button
                onClick={onClick}
                className="ml-4 p-1 rounded-sm opacity-70 transition-opacity hover:opacity-100"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </Alert>
        </div>
      </CSSTransition>
    );
  } else {
    return (
      <CSSTransition appear={true} timeout={500}>
        <div />
      </CSSTransition>
    );
  }
};

export default Flash;
